/**
 * Vehicle Saved Data Loader
 * Loads saved vehicle data (metafields or localStorage) when the user opens the
 * "My Vehicle" section, the PDP vehicle modal, or when on the account page with
 * [data-vehicle-list-item]. The list is built from window.customerSelectedVehicles
 * without loading any CSV. CSV is loaded only when "Add vehicle" / "Change vehicle"
 * dropdowns are used (handled by component-global-ymm and related components).
 */
(function () {
    'use strict';

    const SELECTORS = {
        vehicleBox: '[data-vehicle_box]',
        mobileWrapper: '[data-my-mobile-vehicle]',
        pdpModal: '.pdp-vehicle-model .modal',
        vehicleListItem: '[data-vehicle-list-item]'
    };

    const DEBOUNCE_MS = 50;

    /**
     * Load saved vehicle data from metafields (logged-in) or localStorage (guests),
     * then refresh the My Vehicle list. Does not load CSV.
     * For logged-in customers we always re-read from DOM (metafields) so the header
     * shows the correct default after redirect to another page.
     */
    function ensureSavedDataAndRefreshList() {
        if (typeof vehicleUtility === 'undefined') return;
        var isCustomer = document.body.classList.contains('customer-loggedin');
        var isCustomerB2B = document.body.classList.contains('b2b-customer');
        if (isCustomer && !isCustomerB2B) {
            vehicleUtility._deterMineVehicles();
        } else if (!window.customerSelectedVehicles) {
            vehicleUtility._deterMineVehicles();
        }
        vehicleUtility._refreshVehicleLists();
        if (document.body.classList.contains('template-account')) {
            vehicleUtility._listAccountVehicle();
        }
    }

    /** Debounce refresh so multiple observers firing in quick succession run once. */
    let refreshTimeout;
    function scheduleRefresh() {
        if (refreshTimeout) clearTimeout(refreshTimeout);
        refreshTimeout = setTimeout(function () {
            refreshTimeout = null;
            ensureSavedDataAndRefreshList();
        }, DEBOUNCE_MS);
    }

    /**
     * Single MutationObserver for elements that get class "open" (desktop My Vehicle
     * dropdown and PDP vehicle modal). When any observed element becomes open, schedule refresh.
     */
    function observeOpenElements(vehicleBox, pdpModal) {
        const observer = new MutationObserver(function (mutations) {
            for (let i = 0; i < mutations.length; i++) {
                if (mutations[i].attributeName !== 'class') continue;
                const target = mutations[i].target;
                if (target.classList && target.classList.contains('open')) {
                    scheduleRefresh();
                    break;
                }
            }
        });
        const opts = { attributes: true, attributeFilter: ['class'] };
        if (vehicleBox) observer.observe(vehicleBox, opts);
        if (pdpModal) observer.observe(pdpModal, opts);
    }

    /**
     * When the mobile My Vehicle area becomes visible, schedule refresh.
     */
    function observeMobileMyVehicle(mobileWrapper) {
        if (!mobileWrapper) return;
        const observer = new IntersectionObserver(
            function (entries) {
                if (entries[0] && entries[0].isIntersecting) scheduleRefresh();
            },
            { threshold: 0.01, root: null }
        );
        observer.observe(mobileWrapper);
    }

    /**
     * When user clicks the My Vehicle custom-select (desktop), schedule refresh.
     */
    function bindToggleInteraction(vehicleBox) {
        if (!vehicleBox) return;
        const customSelect = vehicleBox.querySelector('custom-select');
        vehicleBox.addEventListener('click', function (e) {
            if (!customSelect || e.target.closest('custom-select') !== customSelect) return;
            scheduleRefresh();
        }, { passive: true, capture: false });
    }

    /**
     * On account page, when [data-vehicle-list-item] is present, load saved data
     * so the account vehicle list is populated (ensureSavedDataAndRefreshList
     * calls _listAccountVehicle() when template-account).
     */
    function initAccountVehicleList() {
        if (!document.body.classList.contains('template-account')) return;
        const listEl = document.querySelector(SELECTORS.vehicleListItem);
        if (listEl) scheduleRefresh();
    }

    function init() {
        const vehicleBox = document.querySelector(SELECTORS.vehicleBox);
        const mobileWrapper = document.querySelector(SELECTORS.mobileWrapper);
        const pdpModal = document.querySelector(SELECTORS.pdpModal);

        observeOpenElements(vehicleBox, pdpModal);
        observeMobileMyVehicle(mobileWrapper);
        bindToggleInteraction(vehicleBox);
        initAccountVehicleList();
    }

    function scheduleInit() {
        var run = function () { init(); };
        if (typeof requestIdleCallback !== 'undefined') {
            requestIdleCallback(run, { timeout: 800 });
        } else {
            setTimeout(run, 0);
        }
    }
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', scheduleInit);
    } else {
        scheduleInit();
    }
})();
